import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  ApiError,
  fetchMe,
  login,
  logout,
  register,
  resendVerification,
  type LoginPayload,
  type RegisterPayload,
  type User,
} from '@/lib/api';
import { detectTimeZone } from '@/lib/dates';
import { activeLocale } from '@/i18n/locale';

export const sessionKey = ['session'] as const;

/**
 * The signed-in user, or null.
 *
 * Deliberately built on the query cache rather than a separate store: the user
 * is server state that arrives over the same cookie session as everything else,
 * and a second copy in a client store would only be a thing to keep in sync.
 */
export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async (): Promise<User | null> => {
      try {
        return (await fetchMe()).user;
      } catch (cause) {
        // Being signed out is an answer, not a failure. Letting it surface as
        // an error would make every consumer special-case a 401.
        if (cause instanceof ApiError && cause.status === 401) {
          return null;
        }

        throw cause;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Everything cached belongs to whoever was signed in, so the rest of the cache
 * is dropped whenever the session changes — otherwise one account's photos
 * could be painted for another.
 *
 * Order matters. Writing the session first lets the route guard unmount the
 * screens belonging to the old session before their queries are dropped;
 * clearing first leaves those screens mounted with no data, and they refetch
 * immediately against a session that no longer exists. The session entry is
 * deliberately excluded from the removal that follows, since it was just set.
 */
function adoptSession(queryClient: QueryClient, user: User | null): void {
  queryClient.setQueryData(sessionKey, user);

  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== sessionKey[0],
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Omit<LoginPayload, 'timezone'>) =>
      login({ ...payload, timezone: detectTimeZone() }),
    onSuccess: ({ user }) => adoptSession(queryClient, user),
  });
}

/**
 * No session to adopt on success, unlike login: registration ends with a link
 * in an inbox rather than with someone signed in.
 */
export function useRegister() {
  return useMutation({
    // The language the sign-up screen was read in becomes the account's, so the
    // confirmation that follows does not arrive in a language they never chose.
    mutationFn: (payload: Omit<RegisterPayload, 'timezone' | 'language'>) =>
      register({ ...payload, timezone: detectTimeZone(), language: activeLocale() }),
  });
}

export function useResendVerification() {
  return useMutation({ mutationFn: resendVerification });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logout,
    // Runs on failure too: if the session is already gone server-side, keeping
    // the user on screen would strand them on a page that cannot load.
    onSettled: () => adoptSession(queryClient, null),
  });
}
